import React from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Stack,
  Paper,
  IconButton,
  InputAdornment,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Search, Close, RestaurantMenuOutlined } from '@mui/icons-material';
import { PRODUCT_IMAGES } from '../../../../branding/assets';
import {
  menuPalette,
  MENU_CHANNELS,
  CHANNEL_DELIVERY,
} from '../utils/dashboardHelpers';

const DashboardHeader = ({
  stats,
  reorderMode,
  setReorderMode,
  onSaveOrder,
  onCancelOrder,
  savingOrder,
  filterText,
  setFilterText,
  restaurante,
  usarCardapioVirtual,
  selectedChannel,
  onSelectedChannelChange,
  ifoodConnected,
  onOpenIfoodImport,
}) => (
  <Paper
    sx={{
      p: { xs: 2, md: 3 },
      mb: 3,
      position: 'relative',
      overflow: 'hidden',
      border: `2px solid ${menuPalette.blue}`,
      background: `linear-gradient(120deg, rgba(31,79,191,0.08), rgba(242,107,58,0.06)), ${menuPalette.paper}`,
      boxShadow: '0 18px 34px rgba(28,39,76,0.14)',
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        backgroundImage: 'repeating-linear-gradient(90deg, rgba(31,79,191,0.08) 0 6px, transparent 6px 18px)',
        opacity: 0.18,
        pointerEvents: 'none',
      }}
    />
    <Stack spacing={2.5} position="relative" zIndex={1}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
        <Stack direction="row" spacing={2.5} alignItems="center" justifyContent="center" sx={{
          width: '100%',
          py: 3,
          borderRadius: 2,
        }}>
          <Box
            component="img"
            src={PRODUCT_IMAGES.oneMenu}
            alt="One Menu"
            sx={{
              height: 132,
              width: 'auto',
              filter: 'drop-shadow(0 8px 18px rgba(0,0,0,0.08))',
              position: 'relative',
              border: `3px solid ${menuPalette.blue}`,
              borderRadius: 4,
              p: 3,
              backgroundColor: '#fff6f3',
              boxShadow: '0 12px 26px rgba(28,39,76,0.18)',
              '&:after': {
                content: '""',
                position: 'absolute',
                inset: 6,
                border: `1px dashed ${menuPalette.border}`,
                borderRadius: 2,
                pointerEvents: 'none',
              },
            }}
          />
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="space-between" alignItems="center">
          {[{ label: 'Cardápios', value: stats.cardapios }, { label: 'Categorias', value: stats.categorias }, { label: 'Itens', value: stats.itens }].map((stat) => (
            <Box
              key={stat.label}
              sx={{
                px: 1.5,
                py: 1,
                minWidth: 120,
                textAlign: 'center',
                border: `1.5px solid ${menuPalette.blue}`,
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: 2,
                boxShadow: '0 10px 20px rgba(27,43,82,0.12)',
              }}
            >
              <Typography variant="subtitle2" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase', color: menuPalette.blue, fontSize: '0.75rem' }}>
                {stat.label}
              </Typography>
              <Typography variant="h5" sx={{ color: menuPalette.ink, fontWeight: 800 }}>
                {stat.value}
              </Typography>
            </Box>
          ))}
        </Stack>

        {!reorderMode && (ifoodConnected || (restaurante?.checkin_slug && usarCardapioVirtual)) && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center" sx={{ mt: 1 }}>
            {ifoodConnected && (
              <Button
                variant="contained"
                size="medium"
                onClick={onOpenIfoodImport}
                startIcon={<RestaurantMenuOutlined />}
                sx={{
                  minWidth: 180,
                  height: 44,
                  borderRadius: 999,
                  bgcolor: '#EA1D2C',
                  color: '#fff',
                  fontWeight: 800,
                  letterSpacing: '0.04em',
                  px: 2.75,
                  '&:hover': { bgcolor: '#c91524' },
                }}
              >
                Trazer do iFood
              </Button>
            )}
            {restaurante?.checkin_slug && usarCardapioVirtual && MENU_CHANNELS.map((channel) => (
              <Button
                key={channel.code}
                variant={channel.code === CHANNEL_DELIVERY ? 'contained' : 'outlined'}
                size="medium"
                color="secondary"
                onClick={() => window.open(`${window.location.origin}/cardapio/${restaurante.checkin_slug}?canal=${channel.code}`, '_blank')}
                sx={{
                  minWidth: 160,
                  height: 44,
                  borderRadius: 999,
                  boxShadow: channel.code === CHANNEL_DELIVERY ? '0 12px 24px rgba(242,107,58,0.25)' : 'none',
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  px: 2.75,
                }}
              >
                {channel.previewLabel}
              </Button>
            ))}
          </Stack>
        )}
      </Box>
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
        sx={{
          p: { xs: 1.5, md: 2 },
          border: `1.5px dashed ${menuPalette.blue}`,
          borderRadius: 3,
          backgroundColor: 'rgba(255,255,255,0.88)',
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder="Buscar na carta (item, categoria ou cardápio)"
          sx={{
            flexGrow: 1,
            minWidth: '240px',
            '& .MuiOutlinedInput-input': { fontWeight: 600, letterSpacing: '0.02em' },
          }}
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: menuPalette.blue }} />
              </InputAdornment>
            ),
            endAdornment: filterText ? (
              <InputAdornment position="end">
                <Tooltip title="Limpar busca">
                  <IconButton
                    size="small"
                    aria-label="Limpar busca"
                    onClick={() => setFilterText('')}
                    sx={{ color: menuPalette.ink }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : null,
          }}
        />
        {reorderMode && (
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            <Button size="small" onClick={onCancelOrder} disabled={savingOrder}>Cancelar</Button>
            <Button size="small" variant="contained" color="secondary" onClick={onSaveOrder} disabled={savingOrder}>
              {savingOrder ? 'Salvando...' : 'Salvar ordem'}
            </Button>
          </Stack>
        )}
        {!reorderMode && usarCardapioVirtual && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={selectedChannel}
            onChange={(_, next) => next && onSelectedChannelChange?.(next)}
            sx={{
              '& .MuiToggleButton-root': {
                px: 1.6,
                fontWeight: 800,
                letterSpacing: '0.04em',
                borderColor: menuPalette.border,
              },
              '& .Mui-selected': {
                color: '#fff !important',
                backgroundColor: `${menuPalette.blue} !important`,
              },
            }}
          >
            {MENU_CHANNELS.map((channel) => (
              <ToggleButton key={channel.code} value={channel.code}>
                {channel.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      </Box>
    </Stack>
  </Paper>
);

export default DashboardHeader;
